/**
 * 🔴 US-AI-0495-S01 (@UC9800) — NHÃN VAI TIẾNG VIỆT: MỘT bảng chữ, dùng chung domain + api.
 *
 * VÌ SAO Ở ĐÂY, KHÔNG PHẢI Ở `apps/api`:
 * Trợ lý dựng câu trả lời *"Thủ quỹ: Minh"* (v1 `AiQueryService` đã xoá; v2: Sổ năng lực dùng bảng này
 * làm nguồn `GROUP_ROLE_KEYS` — `registry/schema.ts`), còn màn hình Flutter in *"Thủ quỹ"* từ ARB.
 * Nếu tầng api tự giữ một bảng chữ riêng thì chat và màn hình sẽ có HAI bản, và bản lệch là bản
 * người dùng đọc trong hội thoại — đúng điệp khúc *"reuse, không cơ chế thứ hai"* (CLAUDE.md §3.8).
 *
 * 🔴 NGUỒN CHỮ: `apps/mobile/lib/l10n/app_vi.arb:131-134` (`my_groups_role_owner`…`_member`).
 * Đổi một bên PHẢI đổi bên kia — ⚠️ CHỮ nhãn ↔ ARB hiện KHÔNG có chốt máy (`member-role-query.test.ts`
 * từng khoá bit-for-bit, đã xoá cùng v1). `group-role-labels.parity.spec.ts` bên `packages/contracts`
 * chỉ khoá tập KHOÁ khớp `GROUP_ROLE` (contracts dep domain, nên đó là tầng DUY NHẤT đọc được cả hai).
 *
 * ⚠️ `packages/domain` THUẦN — không dep `@goppy/contracts` (chiều phụ thuộc là contracts → domain).
 * Nên khoá vai bị lặp literal ở đây; phép chống-drift đặt ở tầng đọc được CẢ HAI (`packages/contracts`),
 * chứ không phải một bản chép thứ hai không ai đo. (Tiền lệ cũ `collection-form-specs.ts` — v1, đã xoá.)
 */
export const GROUP_ROLE_LABEL_VI = Object.freeze({
  owner: "Trưởng nhóm",
  admin: "Quản trị viên",
  treasurer: "Thủ quỹ",
  member: "Thành viên",
});

export type GroupRoleLabelKey = keyof typeof GROUP_ROLE_LABEL_VI;

/**
 * 🔴 AI2D-ANSWER-WORDING (@UC0458) — tên vai viết THƯỜNG để đặt GIỮA CÂU của trợ lý (câu khoá vai «Việc … do chủ nhóm, quản trị viên hoặc
 * thủ quỹ làm» · dữ liệu làn đọc). Khác `GROUP_ROLE_LABEL_VI` (nhãn CHIP của màn «Nhóm của tôi» — ARB `my_groups_role_owner`): chữ trong câu
 * của app (đổi vai, chuyển quyền, xoá nhóm…) gọi vai sở hữu là «chủ nhóm». MỘT bảng cho mọi câu của trợ lý — trước đây nằm riêng trong
 * `locked-page-hint.ts`, làn đọc lấy nhãn chip ⇒ cùng một cuộc trò chuyện gọi một người là «trưởng nhóm» lẫn «chủ nhóm» (nghiệm thu 17/10 E4).
 */
export const GROUP_ROLE_NOUN_VI: Readonly<Record<GroupRoleLabelKey, string>> = Object.freeze({
  owner: "chủ nhóm",
  admin: "quản trị viên",
  treasurer: "thủ quỹ",
  member: "thành viên",
});

/** Tên vai giữa câu; vai lạ ⇒ `null` (cùng luật `groupRoleLabelVi`: không bịa nhãn). */
export function groupRoleNounVi(role: string): string | null {
  return Object.prototype.hasOwnProperty.call(GROUP_ROLE_NOUN_VI, role) ? GROUP_ROLE_NOUN_VI[role as GroupRoleLabelKey] : null;
}

/**
 * Khoá vai dạng MẢNG ĐÓNG (US-AIV2-0013, #802 L4) — cho `z.enum(...)` của Sổ năng lực và mọi nơi cần liệt kê vai.
 * `satisfies` khoá cứng: thêm vai vào `GROUP_ROLE_LABEL_VI` mà quên ở đây ⇒ lỗi kiểu, không drift lặng.
 */
export const GROUP_ROLE_KEYS = ["owner", "admin", "treasurer", "member"] as const satisfies readonly GroupRoleLabelKey[];

/**
 * Vai QUẢN TRỊ — tập trả lời câu *"ai quản lý nhóm này"*. `member` cố ý ĐỨNG NGOÀI: thành viên
 * thường không phải câu trả lời cho câu hỏi về ban quản trị.
 *
 * Thứ tự trong mảng LÀ thứ tự trình bày trong câu trả lời (Trưởng nhóm trước, rồi Thủ quỹ — người
 * ta hỏi thủ quỹ nhiều hơn hỏi quản trị viên), nên đừng sắp lại cho "gọn abc".
 */
export const GROUP_MANAGER_ROLES = Object.freeze([
  "owner",
  "treasurer",
  "admin",
] as const) satisfies readonly GroupRoleLabelKey[];

/**
 * Vai thô (từ DB) → khoá nhãn. Vai lạ ⇒ `"member"`.
 *
 * ⚠️ Hai hàm, hai chiều fallback CÓ CHỦ ĐÍCH, đừng gộp:
 *  · `normalizeRoleLabelKey` dùng ở tầng ĐỌC DỮ LIỆU — cần một khoá hợp lệ để kiểu chặt lại, và
 *    "vai lạ ⇒ thành viên thường" là an toàn nhất (KHÔNG bịa vai cao hơn).
 *  · `groupRoleLabelVi` dùng ở tầng CÂU CHỮ — vai lạ ⇒ `null` để **không in nhãn nào**, thà thiếu
 *    một chữ còn hơn gọi ai đó là "Thành viên" trong khi họ giữ một vai mới chưa khai nhãn.
 */
export function normalizeRoleLabelKey(role: string): GroupRoleLabelKey {
  return Object.prototype.hasOwnProperty.call(GROUP_ROLE_LABEL_VI, role)
    ? (role as GroupRoleLabelKey)
    : "member";
}

/** Nhãn tiếng Việt của một vai; vai lạ ⇒ `null` (KHÔNG bịa nhãn, không rơi về "Thành viên"). */
export function groupRoleLabelVi(role: string): string | null {
  return Object.prototype.hasOwnProperty.call(GROUP_ROLE_LABEL_VI, role)
    ? GROUP_ROLE_LABEL_VI[role as GroupRoleLabelKey]
    : null;
}
